import { EScreenType, ESdChangeReason, EWFStepType, IProcessStepScreensInfo, IWorkFlow } from "@kuika/kuika-cl-model";
import { IProjectsWithWFResponseDto } from "@kuika/kuika-cl-model/src/dtos/projects-response";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { EScreenRole } from "../../../../shared/utility/screen-role-helper";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaSmallBoldText from "../../../ui-kit/typography/SmallBoldText/SmallBoldText";
import "./screen-process-step-config.scss";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import PropertyRowIcon from "./property-row-icon";

export interface IScreenProcessStepConfigState {
  projectList: IProjectsWithWFResponseDto[] | undefined;
  processStepScreenInfo: IProcessStepScreensInfo | undefined;
  isStepScreenSwitchOn: boolean;
  isExternalProcessSwitchOn: boolean;
  /** Where UPDATE stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  noUpdate: boolean;
  isProjectSelectEnabled: boolean;
  isSwitchLoading: boolean;
}

export interface IScreenProcessStepConfigProps {
  editable: boolean;
  selectedRole: EScreenRole;
  onStepPropertiesChanged: (info: IProcessStepScreensInfo) => {};
}

class ScreenProcessStepConfig extends PureSdComponent<
  IScreenProcessStepConfigProps & IWithSDProps & IWithContextProp,
  IScreenProcessStepConfigState
> {
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IScreenProcessStepConfigProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      projectList: [],
      processStepScreenInfo: undefined,
      isStepScreenSwitchOn: false,
      isExternalProcessSwitchOn: false,
      saveStatus: "idle",
      noUpdate: true,
      isProjectSelectEnabled: false,
      isSwitchLoading: false
    };
  }

  componentDidMount(): void {
    this.getProjectList();
    this.setDefaultSelection();
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidUpdate(
    prevProps: Readonly<IScreenProcessStepConfigProps & IWithSDProps & IWithContextProp>,
    prevState: Readonly<IScreenProcessStepConfigState>,
    snapshot?: any
  ): void {
    if (this.props.sd.sdId != prevProps.sd.sdId) {
      this.setDefaultSelection();
    }
  }

  getProjectList = async () => {
    let projectList: IProjectsWithWFResponseDto[] = [];
    const result = await this.props.sd.getProjectsWithWorkflowData();
    if (result !== undefined) projectList = result;
    if (!projectList.some((p) => p.id === this.props.sd.project.id)) {
      const project: IProjectsWithWFResponseDto = {
        id: this.props.sd.project.id,
        name: this.props.sd.project.name,
        description: this.props.sd.project.description,
        workflows: [] as IWorkFlow[]
      };
      projectList.push(project);
    }
    this.setState({ projectList });
  };

  setDefaultSelection = () => {
    const processStepScreenInfo = this.props.sd.project.processStepScreenIds?.find(
      (p) => p.screenId === this.props.sd.sdId
    );
    this.setState({
      processStepScreenInfo,
      isStepScreenSwitchOn: processStepScreenInfo !== undefined,
      noUpdate: true
    });
  };

  handleProjectChange = async (projectId) => {
    if (projectId == this.state.processStepScreenInfo?.projectId) return;

    const processStepScreenInfo = {
      screenId: this.props.sd.sdId,
      projectId,
      processId: "",
      stepId: "",
      isManaged: false
    };
    this.setState({ processStepScreenInfo, noUpdate: false });
  };

  handleProcessChange = async (processId) => {
    if (processId == this.state.processStepScreenInfo?.processId) return;
    const processStepScreenInfo = {
      screenId: this.props.sd.sdId,
      projectId: this.state.processStepScreenInfo?.projectId || "",
      processId,
      stepId: "",
      isManaged: false
    };
    this.setState({ processStepScreenInfo, noUpdate: false });
  };

  handleStepChange = async (stepId: string) => {
    if (stepId == this.state.processStepScreenInfo?.stepId) return;
    const processStepScreenInfo = {
      screenId: this.props.sd.sdId,
      projectId: this.state.processStepScreenInfo?.projectId || "",
      processId: this.state.processStepScreenInfo?.processId || "",
      stepId,
      isManaged: false
    };
    this.setState({ processStepScreenInfo, noUpdate: false });
  };

  // The assignment stays on screen, so UPDATE is where the save is confirmed: it holds its ✓, and
  // only then do UNDO / UPDATE step back as there is nothing left to update.
  processScreenAssignment = async (): Promise<void> => {
    const { processStepScreenInfo } = this.state;
    if (!processStepScreenInfo) return;
    // Live again once the ✓ replaces the spinner, so a second press would save the same assignment.
    if (!this.saveStatus.canStart) return;

    this.saveStatus.start();
    try {
      await this.props.onStepPropertiesChanged(processStepScreenInfo);
    } catch {
      // The panel stays open, so the button is where the failure is said.
      this.saveStatus.fail();
      return;
    }
    this.saveStatus.succeed((): void => this.setState({ noUpdate: true }));
  };

  undo = () => {
    const processStepScreen = this.props.sd.project.processStepScreenIds?.find(
      (p) => p.screenId === this.props.sd.sdId
    );
    this.setState({
      processStepScreenInfo: processStepScreen,
      noUpdate: true
    });
  };

  getProject = (): IProjectsWithWFResponseDto | undefined => {
    const project = this.state.projectList?.find((p) => p.id === this.state.processStepScreenInfo?.projectId);
    return project;
  };

  getProcess = (): IWorkFlow | undefined => {
    const project = this.getProject();
    if (project === undefined || this.state.processStepScreenInfo?.projectId === undefined) return undefined;
    return project.workflows.find((p) => p.id === this.state.processStepScreenInfo?.processId);
  };

  getStep = () => {
    const process = this.getProcess();
    if (process === undefined || this.state.processStepScreenInfo?.stepId === undefined) return undefined;
    return this.props.sd.findStepRecursively(process, (s) => s.id === this.state.processStepScreenInfo?.stepId);
  };

  onStepScreenSettingsChanged = async (checked: boolean) => {
    this.setState({ isSwitchLoading: true });
    const processScreenData = this.props.sd.project.processStepScreenIds?.find(
      (x) => x.screenId === this.props.sd.sdId
    );

    const project = this.props.sd.project;
    const stepScreenInfo = _.clone(project.processStepScreenIds?.find((x) => x.screenId === this.props.sd.sdId));
    if (checked) {
      this.setState({ isStepScreenSwitchOn: true });
      if (!stepScreenInfo) {
        const processStepScreenInfo = {
          screenId: this.props.sd.sdId,
          projectId: project.id,
          processId: "",
          stepId: "",
          isManaged: false
        };
        this.setState({ processStepScreenInfo });
      } else {
        this.setState({ isStepScreenSwitchOn: true, processStepScreenInfo: stepScreenInfo });
      }
    } else {
      if (stepScreenInfo) {
        const itemIndex = project.processStepScreenIds.findIndex((i) => i.screenId === this.props.sd.sdId);
        if (itemIndex > -1) {
          project.processStepScreenIds.splice(itemIndex, 1);
        }
      }
      this.setState({ isStepScreenSwitchOn: false, processStepScreenInfo: undefined });
      await this.props.sd.updateProject(project);
    }

    this.setState({ isSwitchLoading: false });
  };

  render() {
    const { noUpdate, saveStatus, processStepScreenInfo, isStepScreenSwitchOn } = this.state;
    // The pair stays while UPDATE runs and holds its ✓, not only while there is something to update.
    const isUpdateBarShown: boolean = !noUpdate || saveStatus !== "idle";
    const processList = this.state.projectList?.find(
      (p: IProjectsWithWFResponseDto) => p.id == this.state.processStepScreenInfo?.projectId
    )?.workflows;
    const selectedProcess = processList?.find((p) => p.id == this.state.processStepScreenInfo?.processId) as IWorkFlow;

    const stepList = selectedProcess?.nodes?.steps?.filter(
      (s) =>
        (s.stepType === EWFStepType.ScreenInteraction &&
          !this.props.sd.project.processStepScreenIds?.some(
            (step) => step.stepId === s.id && step.processId === this.state.processStepScreenInfo?.processId
          )) ||
        s.id === this.state.processStepScreenInfo?.stepId
    );
    const rolesToDisplay = [EScreenRole.none, EScreenRole.processStepScreen, EScreenRole.extProcessStepScreen];
    if (!rolesToDisplay.includes(this.props.selectedRole)) return <></>;
    const isManagedScreen = processStepScreenInfo?.isManaged;

    if (this.props.sd.getScreenType() !== EScreenType.Master)
      return (
        <div
          className="kuika-process-step-config kuika-right-panel__card--wrapper"
          style={{
            pointerEvents: !this.state.projectList || this.state.projectList.length == 0 ? "none" : "auto",
            cursor: !this.state.projectList || this.state.projectList.length == 0 ? "not-allowed" : "auto"
          }}
        >
          <div className={`kuika-right-panel__card${isStepScreenSwitchOn ? " kuika-right-panel__card--open" : ""}`}>
            <PropertyRowIcon kind="stepScreen" />
            <div className="kuika-process-step-title">
              {isManagedScreen ? <KuikaSvg name="lock" width={14} height={14} style={{ marginRight: "8px" }} /> : null}
              <KuikaTextMedium>Process Step</KuikaTextMedium>
            </div>
            <KuikaSwitch
              checked={isStepScreenSwitchOn}
              disabled={isManagedScreen || this.state.isSwitchLoading}
              onChange={(checked) => {
                this.onStepScreenSettingsChanged(checked);
              }}
            />
          </div>

          {isStepScreenSwitchOn && (
            <div className="kuika-right-panel__card-body">
              <div className="kuika-process-step-form">
                <div className="kuika-form-item">
                  <label>
                    <KuikaSmallBoldText>Project</KuikaSmallBoldText>
                  </label>
                  {isManagedScreen ? (
                    <div className="kuika-field-value">
                      <KuikaSmallMediumText>{this.getProject()?.name}</KuikaSmallMediumText>
                    </div>
                  ) : (
                    <KuikaSelectbox
                      searchable
                      size="sm"
                      placeholder="Select a Project"
                      value={this.state.processStepScreenInfo?.projectId}
                      onChange={(val) => this.handleProjectChange(val as string)}
                      options={this.state.projectList
                        ?.sort((a, b) => {
                          const nameA = a.name.toUpperCase();
                          const nameB = b.name.toUpperCase();
                          if (nameA < nameB) return -1;
                          if (nameA > nameB) return 1;
                          return 0;
                        })
                        .map((p) => ({
                          value: p.id,
                          label: p.name
                        }))}
                    />
                  )}
                </div>

                <div className="kuika-form-item">
                  <label>
                    <KuikaSmallBoldText>Process</KuikaSmallBoldText>
                  </label>
                  {isManagedScreen ? (
                    <div className="kuika-field-value">
                      <KuikaSmallMediumText>{this.getProcess()?.name}</KuikaSmallMediumText>
                    </div>
                  ) : (
                    <KuikaSelectbox
                      searchable
                      size="sm"
                      placeholder="Select a Process"
                      value={this.state.processStepScreenInfo?.processId}
                      onChange={(val) => this.handleProcessChange(val as string)}
                      options={processList?.map((wf) => {
                        const isExternalProject =
                          this.state.processStepScreenInfo?.projectId !== this.props.sd.project.id;
                        const isInactive = wf.inactived === true;
                        const disabled = isExternalProject && isInactive;
                        return {
                          value: wf.id,
                          label: disabled ? `${wf.displayName} (Inactive)` : wf.displayName,
                          disabled
                        };
                      })}
                    />
                  )}
                </div>

                <div className="kuika-form-item">
                  <label>
                    <KuikaSmallBoldText>Step</KuikaSmallBoldText>
                  </label>
                  {isManagedScreen ? (
                    <div className="kuika-field-value">
                      <KuikaSmallMediumText>{this.getStep()?.name}</KuikaSmallMediumText>
                    </div>
                  ) : (
                    <KuikaSelectbox
                      searchable
                      size="sm"
                      placeholder="Select a Step"
                      value={this.state.processStepScreenInfo?.stepId}
                      onChange={(val) => this.handleStepChange(val as string)}
                      options={stepList?.map((step) => ({
                        value: step.id,
                        label: step.name ?? step.id
                      }))}
                    />
                  )}
                </div>

                {isUpdateBarShown && (
                  <div className="kuika-action-buttons">
                    <KuikaButton
                      disabled={noUpdate || saveStatus === "loading"}
                      type="text"
                      color="neutral"
                      onClick={() => this.undo()}
                    >
                      UNDO
                    </KuikaButton>
                    <div className="update-button-wrapper">
                      <KuikaButton
                        color={!noUpdate ? "danger" : "default"}
                        type="text"
                        status={saveStatus}
                        onClick={() => this.processScreenAssignment()}
                      >
                        UPDATE
                      </KuikaButton>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );

    return <></>;
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return true;
  };
}

const screenProcessStepConfig = withContext<IScreenProcessStepConfigProps>(
  withSD<IScreenProcessStepConfigProps>(ScreenProcessStepConfig)
);
export { screenProcessStepConfig as ScreenProcessStepConfig };
